import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import TeamService from "services/Team.service";
import theme from "shared/assets/theme";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import TeamCard from "webComponentsScreens/teams-list/TeamCard.component";
import UpsertTeamForm from "webComponentsScreens/teams-list/UpsertTeamForm.component";
import Banner from "webComponentsShared/Banner.component";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "white",
  },
  banner: {
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
  },
  scrollContainer: {
    flex: 1,
  },
  cardsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    paddingBottom: 20,
    padding: 10,
  },
  noResultsText: {
    color: theme.color.darkGrey,
    marginTop: 20,
  },
});

export default function TeamsListScreen({ route }) {
  const { getTeams, teamList, getPersons } = useContext(OrganizationContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { showToast } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isTeamSelected, setIsTeamSelected] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [teamToAdd, setTeamToAdd] = useState(null);
  const [teamPersonBatchList, setTeamPersonBatchList] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getTeams(), getPersons()]);
      setIsLoading(false);
    };
    fetchData();
  }, [getTeams, getPersons]);

  useEffect(() => {
    if (
      route.params?.selectedTeamId &&
      route.params?.openModal &&
      teamList.length > 0 &&
      !isModalOpen
    ) {
      const teamToOpen = teamList.find(
        (team) => team.teamId === route.params.selectedTeamId,
      );
      if (teamToOpen) {
        openTeamModal(teamToOpen);
      }
    }
  }, [route.params, teamList, isModalOpen, openTeamModal]);

  const handleTeamUpsert = async (aTeam) => {
    try {
      const res = await TeamService.upsertTeam(aTeam);

      if (res.status === 350) {
        showToast(res.message, "error");
        return;
      }
      if (res.message && res.payload) {
        await getTeams();
      }
      showToast(`Successfully saved ${aTeam.team.name}.`, "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Error saving team:", error);
      }
      showToast(`Failed to save ${aTeam.team.name}.`, "error");
    }
  };

  const handleTeamEdits = async () => {
    const teamData = {
      ...teamToAdd,
      personIds: teamPersonBatchList || [],
    };

    await handleTeamUpsert({ team: teamData });
    setIsModalOpen(false);
    setIsTeamSelected(false);
    setSelectedTeam(null);
    setCanSave(false);
    setTeamToAdd(null);
  };

  const onModalClose = () => {
    canSave
      ? showConfirmation({
        prompt:
          "You have unsaved changes. Are you sure you want to discard them?",
        onConfirm: closeModal,
      })
      : closeModal();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTeamToAdd(null);
    setIsTeamSelected(false);
    setSelectedTeam(null);
  };

  const openTeamModal = useCallback(
    (aTeam) => {
      if (aTeam._associations?.isDefault) {
        showToast("Default teams cannot be edited.", "error");
        return;
      }
      setSelectedTeam(aTeam);
      setIsTeamSelected(true);
      setIsModalOpen(true);
    },
    [showToast],
  );

  const openCreateModal = () => {
    setTeamToAdd(null);
    setIsModalOpen(true);
    setIsTeamSelected(false);
  };

  const handleModalSave = async () => {
    const teamData = {
      name: teamToAdd.name,
      personIds: teamPersonBatchList || [],
    };

    await handleTeamUpsert({ team: teamData });
    setTeamToAdd(null);
    setIsModalOpen(false);
    setIsTeamSelected(false);
    setCanSave(false);
  };

  const handleTeamDelete = async (team) => {
    const res = await TeamService.deleteTeam(team.teamId);
    if (res.message && res.payload) {
      await getTeams();
    }
    showToast(`Successfully deleted ${team.name}.`, "success");
  };

  const onTeamDelete = (team) => {
    const people = team._associations?.TeamPersons || [];
    const dependencies =
      team._associations?.OrganizationSystemDependencies || [];
    showConfirmation({
      prompt: (
        <View style={{ alignItems: "center", gap: 20, padding: 10 }}>
          <Text>
            Are you sure you want to permanently delete the team "{team.name}"?
          </Text>
          {people.length > 0 && (
            <View style={{ gap: 10, alignItems: "center" }}>
              <Text>This action will unassign the following people:</Text>
              <ScrollView
                style={{ maxHeight: 150, width: "max-content" }}
                contentContainerStyle={{
                  alignItems: "center",
                  paddingHorizontal: 10,
                }}
              >
                <ul style={{ fontSize: 15, textAlign: "left" }}>
                  {people.map((person) => (
                    <li key={person.personId}>
                      {person.firstName} {person.lastName}
                    </li>
                  ))}
                </ul>
              </ScrollView>
            </View>
          )}
          {dependencies.length > 0 && (
            <View style={{ gap: 10, alignItems: "center" }}>
              <Text>
                and will remove the following dependencies for this team:
              </Text>
              <ScrollView
                style={{ maxHeight: 150, width: "max-content" }}
                contentContainerStyle={{
                  alignItems: "center",
                  paddingHorizontal: 10,
                }}
              >
                <ul style={{ fontSize: 15, textAlign: "left" }}>
                  {dependencies.map((dependency) => (
                    <li key={dependency.id}>{dependency.systemTitle}</li>
                  ))}
                </ul>
              </ScrollView>
            </View>
          )}
        </View>
      ),
      onConfirm: () => handleTeamDelete(team),
    });
  };

  const filteredTeams = teamList.filter((team) =>
    team.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (isLoading) {
    return <ActivityIndicator size="large" />;
  }

  return (
    <View style={classes.root}>
      <Banner classes={classes.banner}>
        <SearchBar
          placeholder="Search teams..."
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        <CustomButton
          title="Create new team"
          variant="dark"
          isLoading={isLoading}
          onPress={() => openCreateModal()}
          icon="plus-circle-outline"
        />
      </Banner>
      <ScrollView
        style={classes.scrollContainer}
        contentContainerStyle={{ flexGrow: 1 }}
        showsHorizontalScrollIndicator={false}
        horizontal={false}
      >
        <View style={classes.cardsContainer}>
          {filteredTeams.map((team) => {
            const teamPersons = team._associations?.TeamPersons || [];
            return (
              <TeamCard
                team={team}
                size={teamPersons.length}
                onPress={() => openTeamModal(team)}
                key={team.teamId}
                onDelete={() => onTeamDelete(team)}
              />
            );
          })}
          {filteredTeams.length === 0 && (
            <Text style={classes.noResultsText}>
              {searchQuery ? "No teams match your search." : "No teams found."}
            </Text>
          )}
        </View>
      </ScrollView>
      <CustomModal
        title={isTeamSelected ? "Edit Team" : "Create Team"}
        open={isModalOpen}
        canSave={canSave}
        onClose={onModalClose}
        onSave={isTeamSelected ? handleTeamEdits : handleModalSave}
        scroll
      >
        <UpsertTeamForm
          setCanSave={setCanSave}
          team={selectedTeam}
          setTeamPersonBatchList={setTeamPersonBatchList}
          setTeamToAdd={setTeamToAdd}
        />
      </CustomModal>
    </View>
  );
}
